import moment from 'moment';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { CellProps } from 'react-table';
import { AppContext } from '../../../../appContext/AppContext';
import { TableCustomCell } from '../../../../component/table/components/TableCustomCell';
import { TableCustomHeader } from '../../../../component/table/components/TableCustomHeader';
import { TableCustom } from '../../../../component/table/table-custom/TableCustom';
import { APP_DATE_FORMAT, CODE, KEY_DS_DANH_MUC_TIEP_DON, RESPONSE_MESSAGE, SELECTION_MODE, VARIABLE_STRING } from '../../../../utils/Constant';
import { convertToStringWithDelimiter, formatDateTime } from '../../../../utils/FormatUtils';
import { deleteHealthInsuranceCard, getHealthInsuranceCards, createHealthInsuranceCard, updateHealthInsuranceCard } from '../../../services/TiepNhanServices';
import CustomIconButton from '../../../../component/custom-icon-button/CustomIconButton';
import { ConfirmDialog } from '../../../../component/ConfirmDialog';
import InsuranceInfo from './InsuranceInfo';
import { Form, Formik } from 'formik';
import { IDanhSachDanhMuc } from '../../../models/TiepDonModel';
import { getIndexedDBItem } from '../../../../utils/IndexedDB';
import * as Yup from 'yup';
import { daySchema, monthSchema, yearSchema } from "../../../../utils/ValidationSchemaUtils";
import { toast } from 'react-toastify';
import useReadQRCode from '../../../hooks/useReadQRCode';
import { CODE_DOI_TUONG } from '../../../constants/PhanHeTiepNhan';

type Props = {
	open: boolean;
	onClose: () => void;
	baseValues: any;
	handleSetInsValuesFromBase: any;
	resetVisitObjectType: any;
};

const InsuranceCardsDialog = ({ open, onClose, baseValues, handleSetInsValuesFromBase, resetVisitObjectType }: Props) => {
	const formRef = useRef<any>(null);
	const { readBHYTQRCode, getBirthDateInfo } = useReadQRCode();

	let validationSchema = Yup.object({
		insurance: Yup.object().shape({
			insFromDay: daySchema,
			insFromMonth: monthSchema,
			insFromYear: yearSchema,

			insToDay: daySchema,
			insToMonth: monthSchema,
			insToYear: yearSchema,

			insNgaymienchitraDay: daySchema,
			insNgaymienchitraMonth: monthSchema,
			insNgaymienchitraYear: yearSchema,

			insNgaydu5namDay: daySchema,
			insNgaydu5namMonth: monthSchema,
			insNgaydu5namYear: yearSchema,
		})
	});

	const columns = useMemo(() => {
		return [
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="STT"
						className="text-center text-light min-w-40"
					/>
				),
				id: 'STT',
				Cell: ({ ...props }) => (
					<TableCustomCell className="text-center" data={props.row.index + 1} />
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Mã thẻ"
						className="text-center text-light min-w-100px"
					/>
				),
				id: 'insCode',
				Cell: ({ row }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-100px"
						data={row.original.insCode}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Hạn thẻ"
						className="text-center text-light min-w-200px"
					/>
				),
				id: 'Hạn thẻ',
				Cell: ({ row }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-200px"
						data={`${convertToDateString(
							row.original.insFromDay,
							row.original.insFromMonth,
							row.original.insFromYear
						)} - ${convertToDateString(
							row.original.insToDay,
							row.original.insToMonth,
							row.original.insToYear
						)}`}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Thời gian áp dụng"
						className="text-center text-light min-w-200px"
					/>
				),
				id: 'Thời gian áp dụng',
				Cell: ({ row: { original } }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-125px"
						data={formatDateTime(original?.effectiveFrom, APP_DATE_FORMAT) + " - " + formatDateTime(original?.effectiveTo, APP_DATE_FORMAT)}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Nơi KCBBĐ"
						className="text-center text-light min-w-250px"
					/>
				),
				id: 'insKcbbdName',
				Cell: ({ row }: CellProps<any>) => (
					<TableCustomCell
						className="min-w-250px"
						data={convertToStringWithDelimiter([
							row.original.insKcbbdCode,
							row.original.insKcbbdName,
						])}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Tuyến KCB"
						className="text-center text-light min-w-250px"
					/>
				),
				id: 'Tuyến KCB',
				Cell: ({ row }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-250px"
						data={convertToStringWithDelimiter([
							row.original.insLevelName,
							row.original.insCoveragePercentage,
						])}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Ngày miễn cùng chi trả"
						className="text-center text-light min-w-175px"
					/>
				),
				id: 'Ngày miễn cùng chi trả',
				Cell: ({ row: { original } }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-175px"
						data={convertToDateString(
							original?.insNgaymienchitraDay,
							original?.insNgaymienchitraMonth,
							original?.insNgaymienchitraYear
						)}
					/>
				),
			},
			{
				Header: (props: any) => (
					<TableCustomHeader
						tableProps={props}
						title="Ngày đủ 5 năm liên tục"
						className="text-center text-light min-w-175px"
					/>
				),
				id: 'Ngày đóng đủ 5 năm',
				Cell: ({ row: { original } }: CellProps<any>) => (
					<TableCustomCell
						className="text-center min-w-175px"
						data={convertToDateString(
							original?.insNgaydu5namDay,
							original?.insNgaydu5namMonth,
							original?.insNgaydu5namYear
						)}
					/>
				),
			},
		];
	}, []);

	const columnsActions = {
		Header: (props: any) => (
			<TableCustomHeader<any>
				tableProps={props}
				title={'Thao tác'}
				className="text-center text-light min-w-80px"
			/>
		),
		id: 'actions',
		Cell: ({ ...props }) => (
			<div className="d-flex justify-content-center">
				<CustomIconButton
					variant="edit"
					onClick={() => {
						formRef.current?.setFieldValue([VARIABLE_STRING.INSURANCE], props.cell.row.original);
					}}
				>
					<i className="bi bi-pencil-square text-navy"></i>
				</CustomIconButton>
				<CustomIconButton
					variant="delete"
					onClick={() => {
						setSelectedRow(props.cell.row.original);
						setOpenConfirmDelete(true);
					}}
				>
					<i className="bi bi-trash3-fill text-danger"></i>
				</CustomIconButton>
			</div>
		),
	};

	const { setIsLoading } = useContext(AppContext);
	const [data, setData] = useState([]);
	const [selectedRow, setSelectedRow] = useState<any>(null);
	const [openConfirmDelete, setOpenConfirmDelete] = useState<boolean>(false);
	const [danhSachDanhMuc, setDanhSachDanhMuc] = useState<IDanhSachDanhMuc>();

	useEffect(() => {
		if (baseValues?.visit?.id) {
			getTableData(baseValues?.visit?.id);
		}
		handleGetDsDanhMuc();
	}, []);

	const searchBarcode = async (value: string, setFieldValue: any) => {
		const QR_SEPARATOR = '|';
		const QR_CCDD_SEPARATOR_COUNT = 7;

		if (value.split(QR_SEPARATOR).length > QR_CCDD_SEPARATOR_COUNT) {
			const data = await readBHYTQRCode(value);
			onReadBHYTQRCode(data, setFieldValue);
			return;
		}
	};

	const onReadBHYTQRCode = (data: any, setFieldValue: any) => {
		let maDoiTuong = '', maQuyenLoi = '', maTinhThanhPho = '', maSoBHXH = '';
		if (data.maSoBHYT && data.maSoBHYT.length === 10) {
			maSoBHXH = data.maSoBHYT;
		} else if (data.maSoBHYT) {
			maDoiTuong = data.maSoBHYT.slice(0, 2);
			maQuyenLoi = data.maSoBHYT.slice(2, 3);
			maTinhThanhPho = data.maSoBHYT.slice(3, 5);
			maSoBHXH = data.maSoBHYT.slice(5);
		}

		const initNgayThangNam = { ngaySinh: "", thangSinh: "", namSinh: "" };
		const { ngaySinh: insFromDay, thangSinh: insFromMonth, namSinh: insFromYear } =
			data.giaTriSuDungTuNgay ? getBirthDateInfo(data.giaTriSuDungTuNgay) : initNgayThangNam;

		const { ngaySinh: insToDay, thangSinh: insToMonth, namSinh: insToYear } =
			data.giaTriSuDungDenNgay ? getBirthDateInfo(data.giaTriSuDungDenNgay) : initNgayThangNam;

		const {
			ngaySinh: insNgaydu5namDay,
			thangSinh: insNgaydu5namMonth,
			namSinh: insNgaydu5namYear,
		} = getBirthDateInfo(data.thoiDiem5NamLienTuc);

		setFieldValue(VARIABLE_STRING.INSURANCE, {
			insCodeDoiTuong: maDoiTuong,
			insCodeMucHuong: maQuyenLoi,
			insCodeNoiPhatHanh: maTinhThanhPho,
			insCodeMaBhxh: maSoBHXH,
			insKcbbdCode: data.maNoiKCB ? data.maNoiKCB.replace(/[-\s]/g, '') : "",
			insKcbbdName: data.tenNoiKCB ?? "",
			insFromDay,
			insFromMonth,
			insFromYear,
			insToDay,
			insToMonth,
			insToYear,
			insNgaydu5namDay,
			insNgaydu5namMonth,
			insNgaydu5namYear,
		});
	};

	const handleGetDsDanhMuc = async () => {
		try {
			const responseDsDanhmuc: IDanhSachDanhMuc = await getIndexedDBItem(
				KEY_DS_DANH_MUC_TIEP_DON
			);
			if (responseDsDanhmuc) {
				setDanhSachDanhMuc(responseDsDanhmuc);
			}

		} catch (error) {
			console.error(error);
		}
	};

	const getTableData = async (visitId: string) => {
		try {
			setIsLoading(true);
			const { code, data } = await getHealthInsuranceCards({
				visitId,
			});

			if (CODE.SUCCESS !== code) {
				throw new Error(data);
			}

			setData(data.content);
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleConfirmDelete = async () => {
		try {
			setIsLoading(true);
			const { code, data } = await deleteHealthInsuranceCard(selectedRow?.id);

			if (CODE.SUCCESS !== code) {
				toast.warn(data?.[0]?.errorMessage || RESPONSE_MESSAGE.ERROR);
			} else {
				if (data) {
					handleSetInsValuesFromBase(data);
				} else {
					handleSetInsValuesFromBase({});
					resetVisitObjectType(CODE_DOI_TUONG.VIEN_PHI);
				}
				baseValues?.visit?.id && getTableData(baseValues?.visit?.id);
				toast.success(RESPONSE_MESSAGE.DELETE.SUCCESS);
			}
			setOpenConfirmDelete(false);
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const convertToDateString = (day: number, month: number, year: number) => {
		if (!year) return '';

		return moment([year, month - 1, day]).format('DD/MM/YYYY');
	};

	const handleFormSubmit = async (values: any) => {
		try {
			setIsLoading(true);
			let submitData = { 
				...values?.[VARIABLE_STRING.INSURANCE],
				visitId: baseValues?.visit?.id,
				patientId: baseValues?.patient?.id,
				insCode: `${values?.[VARIABLE_STRING.INSURANCE]?.insCodeDoiTuong || ''}${values?.[VARIABLE_STRING.INSURANCE]?.insCodeMucHuong || ''}${values?.[VARIABLE_STRING.INSURANCE]?.insCodeNoiPhatHanh || ''}${values?.[VARIABLE_STRING.INSURANCE]?.insCodeMaBhxh || ''}`,
				insLevelCode: values?.[VARIABLE_STRING.INSURANCE]?.examLevel?.code || values?.[VARIABLE_STRING.INSURANCE]?.insLevelCode,
				insLevelName: values?.[VARIABLE_STRING.INSURANCE]?.examLevel?.name || values?.[VARIABLE_STRING.INSURANCE]?.insLevelName,
			};
			const { code, data } = submitData?.id ? await updateHealthInsuranceCard(submitData?.id, submitData, handleFormSubmit) : await createHealthInsuranceCard(submitData, handleFormSubmit);

			if (CODE.SUCCESS === code) {
				toast.success(`${submitData?.id ? 'Cập nhật' : 'Thêm mới'} thẻ thành công`);
				handleSetInsValuesFromBase(data);
				baseValues?.visit?.visitObjectType?.code !== CODE_DOI_TUONG.BAO_HIEM && resetVisitObjectType(CODE_DOI_TUONG.BAO_HIEM);
			} else {
				let errorMessage = data?.[0]?.errorMessage || data?.message;
				toast.warn(errorMessage);
			}
			baseValues?.visit?.id && getTableData(baseValues?.visit?.id);
		} catch (error) {
			toast.error(RESPONSE_MESSAGE.ERROR);
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Modal
			dialogClassName="h-modal"
			size="xl"
			show={open}
			onHide={onClose}
			backdrop="static"
			centered
		>
			<Formik
				initialValues={{
					[VARIABLE_STRING.INSURANCE]: {},
				}}
				validationSchema={validationSchema}
				onSubmit={handleFormSubmit}
				innerRef={formRef}
			>
				{({ values, setFieldValue }) => (
					<Form>
						<Modal.Header closeButton>
							<Modal.Title>Danh sách thẻ BHYT</Modal.Title>
						</Modal.Header>

						<Modal.Body className='min-h-200px'>
							<Row>
								<Col xs="5">
									<InsuranceInfo
										isDisabled={false}
										isBHYT={true}
										danhSachDanhMuc={danhSachDanhMuc}
										handleChangeInsurance={(name: string, value: any) => {
											setFieldValue(VARIABLE_STRING.INSURANCE, {
												...values?.insurance,
												[name]: value
											})
										}}
										baseValues={baseValues}
										onReadBHYTQRCode={(data: any) => onReadBHYTQRCode(data, setFieldValue)}
										searchBarcode={(data: any) => searchBarcode(data, setFieldValue)}
										isFromInsuranceCardsDialog={true}
									/>
								</Col>
								<Col xs="7">
									<TableCustom
										data={data}
										columns={columns}
										columnsActions={columnsActions}
										maxHeight={500} minHeight={350}
										selectionMode={SELECTION_MODE.SINGLE_NO_RADIO_BUTTON}
									/>
								</Col>
							</Row>
							{openConfirmDelete && (
								<ConfirmDialog
									className="z-index-1060"
									show={openConfirmDelete}
									title="Thông báo"
									message="Bạn có chắc chắn muốn xóa thẻ này không ?"
									yes="Xác nhận"
									close="Đóng"
									onCloseClick={() => {
										setOpenConfirmDelete(false);
									}}
									onYesClick={handleConfirmDelete}
								/>
							)}
						</Modal.Body>

						<Modal.Footer>
							<Button className="btn-fill spaces min-w-80" type="submit">
								{formRef.current?.values?.[VARIABLE_STRING.INSURANCE]?.id ? 'Cập nhật' : 'Thêm'}
							</Button>
							<Button className="btn-secondary spaces min-w-80" onClick={onClose}>
								Đóng
							</Button>
						</Modal.Footer>
					</Form>
				)}
			</Formik>
		</Modal>
	);
};

export default InsuranceCardsDialog;
